/**
 * Wire types and view types for the web standings screen.
 *
 * The wire types mirror `POST /standings/get` (`sports-api`, private zone)
 * one for one. They are declared locally rather than imported from the SDK
 * because the SDK's `private` barrel re-exports hooks, not types.
 */

export type typeStandingsColumn = { key: string; label: string };
export type typeStandingsGroupingOption = { key: string; label: string };

export type typeStandingsCellDto = {
  key: string;
  value?: number | null;
  display: string;
};

export type typeStandingsRowDto = {
  teamId: string;
  /** The provider rank. This app never reads it; `rankLabel` is the display form. */
  rank: number;
  rankLabel: string;
  teamName: string;
  teamAbbr: string;
  /** The resolved thumbnail URL from media-api, or null for the initials plate (ISSUE-036). */
  logoUrl?: string | null;
  /** The nickname or school, once sports-api sends it (Scores UX spec 1.4). */
  shortName?: string | null;
  cells: typeStandingsCellDto[];
};

export type typeStandingsGroupDto = {
  key: string;
  label: string;
  parentLabel?: string | null;
  rows: typeStandingsRowDto[];
};

export type typeStandingsDataDto = {
  leagueId: string;
  leagueName: string;
  seasonLabel: string;
  grouping: string;
  groupings: typeStandingsGroupingOption[];
  primaryColumns: typeStandingsColumn[];
  secondaryColumns: typeStandingsColumn[];
  groups: typeStandingsGroupDto[];
  emptyReason?: string | null;
};

export type typeStandingsEnvelope = {
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  } | null;
  data?: typeStandingsDataDto | null;
};

export type typeStandingsTableRow = {
  /** The sportsmet team id. Used as the React key and as the row link's `teamId`. */
  id: string;
  rankLabel: string;
  teamName: string;
  teamAbbr: string;
  /** The team's logo, or null for its abbreviation plate (ISSUE-1087). */
  logoUrl: string | null;
  /** The nickname or school, or null until the server sends it. */
  shortName?: string | null;
  values: Record<string, string>;
};

export type typeStandingsGroupView = {
  key: string;
  /** The parent conference, printed once above the first group under it. */
  sectionHeader: string | null;
  title: string;
  rows: typeStandingsTableRow[];
};

export type typeStandingsView =
  | { kind: "loading" }
  | { kind: "error"; code: string }
  | { kind: "empty"; title: string; body: string }
  | {
      kind: "ready";
      leagueId: string;
      leagueName: string;
      seasonLabel: string;
      groupingOptions: typeStandingsGroupingOption[];
      selectedGroupingKey: string;
      primaryColumns: typeStandingsColumn[];
      secondaryColumns: typeStandingsColumn[];
      groups: typeStandingsGroupView[];
    };

export const STANDINGS_EMPTY_TITLE = "No standings yet";
export const STANDINGS_EMPTY_BODY =
  "This league's table shows up once the season starts.";
export const STANDINGS_ERROR_TITLE = "Standings didn't load";
export const STANDINGS_BACK_LABEL = "Back to games";

/** The standings screen's own title, "WNBA standings" (spec 10). */
export function standingsTitle(leagueName: string): string {
  return `${leagueName} standings`;
}
export const STANDINGS_GROUPING_LABEL = "Standings grouping";
export const STANDINGS_LOADING_LABEL = "Loading standings";
export const STANDINGS_TEAM_HEADER = "Team";
/**
 * A cell the server had no value for. Kept identical to the shipped mobile
 * table: `apps/mobile/src/ui/StandingsTableLogic.ts` renders a placeholder
 * hyphen, not an em dash.
 */
export const STANDINGS_MISSING_CELL = "-";
